import { useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableInputWithX } from "./TableInput";
import { cn } from "../ClassName";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

interface Props {
  /**
   * @type string
   * @description Tailwind CSS string for styling
   */
  className: string;
  /**
   * @type string
   * @description The initially selected option display string
   */
  initialOptionDisplay: string;
  /**
   * @type any[]
   * @description List of options to be displayed.
   */
  options: any[];
  /**
   * @type (query: string) => void
   * @description function that takes in the query and sets the filtered options accordingly
   */
  setOptions: (query: string) => void;
  /**
   * @type (option: any) => string
   * @description function takes an option and returns the string to be displayed for it in the dropdown
   */
  getOptionDisplay: (option: any) => string;
  /**
   * @type string
   * @description Placeholder string for the dropdown
   */
  placeholder: string;
  /**
   * @type any
   * @description Takes the selected option string and sets the state of the parent component
   */
  setState: (selectedOptionDisplay: string) => void;
  /**
   * @type string
   * @description Small header for the dropdown
   */
  dropdownText?: string;
  /**
   * @type string
   * @description Warning or Error if the input has an active warning
   */
  hasActiveWarning?: string;
  /**
   * @type number
   * @description Imperative signal: when this value changes (after the first
   * render), the dropdown focuses its input and expands. Use to trigger
   * focus+expand from outside in response to a discrete user action (e.g. a
   * paste into a sibling input). Pass an incrementing counter from the parent.
   */
  triggerExpand?: number;
  /**
   * @type string
   * @description Value of the previous text input used to focus current input accordingly
   */
  previous?: any;
  /**
   * @type boolean
   * @description True when we want to use the dropdown for search without suggestion.
   */
  withoutSuggestion?: boolean;
  /**
   * @type string
   * @description Target selector for PLaywright tests.
   */
  dataTestId?: string;
  /**
   * @type string
   * @description Tailwind CSS string for custom styling on Dropdown Item.
   */
  dropdownClassName?: string;
  /**
   * @type () => void
   * @description Function to be called when the clear button is clicked.
   */
  onClear?: () => void;
}
interface itemProps {
  option: any;
  getOptionDisplay: (option: any) => string;
  selectedOptionDisplay: string;
  updateSelection: (option: any) => void;
}

function DropdownItem(props: itemProps) {
  const optionDisplay: string = props.getOptionDisplay(props.option);
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    // Case insensitive match
    const check =
      props.selectedOptionDisplay.toLowerCase() === optionDisplay.toLowerCase();
    if (check) {
      setChecked(check);
      props.updateSelection(optionDisplay);
    } else {
      setChecked(false);
    }
  }, [props.selectedOptionDisplay]);

  return (
    <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4">
      <span className="tw-align-text-bottom">{optionDisplay}</span>
      {checked && <FontAwesomeIcon icon={faCheck} className="tw-text-blue-1" />}
    </div>
  );
}

export default function SuggestionDropdown(props: Props) {
  const [selectedOptionDisplay, setSelectedOptionDisplay] =
    useState<string>("");
  const [expanded, setExpanded] = useState<boolean>(false);

  const updateSelection = (query: string) => {
    setSelectedOptionDisplay(query);
    props.setState(query);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks the last triggerExpand value we've reacted to. Initializing with
  // the current prop value (instead of a boolean "is first mount" guard)
  // means we correctly skip on initial mount AND on remounts where the parent
  // signal lands on the same value the dropdown already saw.
  const prevTriggerExpandRef = useRef(props.triggerExpand);

  useEffect(() => {
    setSelectedOptionDisplay(props.initialOptionDisplay);
    props.setOptions(props.initialOptionDisplay);
  }, [props.initialOptionDisplay]);

  useEffect(() => {
    if (prevTriggerExpandRef.current === props.triggerExpand) return;
    prevTriggerExpandRef.current = props.triggerExpand;
    inputRef.current?.focus();
    setExpanded(true);
    // focus() synchronously fires the onFocus handler, which calls
    // setOptions(selectedOptionDisplay) using the pre-paste state value.
    // Re-call setOptions here so the final, authoritative call uses the
    // latest initialOptionDisplay (the just-pasted text).
    props.setOptions(props.initialOptionDisplay);
  }, [props.triggerExpand]);

  return (
    <div
      className={props.className}
      onBlur={() => {
        setExpanded(false);
      }}
    >
      <TableInputWithX
        type="text"
        placeholder={props.placeholder}
        innerRef={inputRef}
        onFocus={() => {
          props.setOptions(selectedOptionDisplay);
          setExpanded(true);
        }}
        setValue={(value) => {
          props.setOptions(value);
          if (!props.withoutSuggestion) {
            updateSelection(value);
          } else {
            setSelectedOptionDisplay(value);
          }
        }}
        value={selectedOptionDisplay}
        className={` ${
          props.hasActiveWarning === "Warning"
            ? "tw-border-[1px] tw-border-orange-1 tw-bg-orange-light-1"
            : props.hasActiveWarning === "Error"
              ? "tw-border-[1px] tw-border-red-1 tw-bg-red-light-1"
              : ""
        } tw-w-full`}
        dataTestId={props.dataTestId}
        customClear={
          props.onClear
            ? () => {
                props.onClear?.();
                updateSelection("");
                props.setOptions("");
              }
            : undefined
        }
      />
      {expanded &&
      (props.options.length > 0 || selectedOptionDisplay.length > 0) ? (
        <div className="tw-relative tw-z-[101]">
          <ul
            className={cn(
              "roundedScroll tw-absolute tw-mt-[4px] tw-max-h-[480px] tw-min-w-[240px] tw-cursor-pointer tw-overflow-y-auto tw-rounded-[4px] tw-bg-white tw-p-0 tw-py-[8px] tw-shadow-[0px_2px_8px_0px_rgba(0,0,0,0.25)]",
              props.dropdownClassName,
            )}
          >
            {props.options.map((option, index) => {
              return (
                <li
                  key={index}
                  className={cn(
                    selectedOptionDisplay.toLowerCase() ===
                      props.getOptionDisplay(option).toLowerCase() &&
                      "tw-bg-blue-light-1",
                    `tw-relative tw-block tw-min-h-[40px] tw-overflow-hidden tw-break-words tw-p-[10px] tw-align-middle tw-text-[14px] hover:tw-bg-blue-light-1`,
                  )}
                  onMouseDown={() =>
                    updateSelection(props.getOptionDisplay(option))
                  }
                >
                  <DropdownItem
                    option={option}
                    getOptionDisplay={props.getOptionDisplay}
                    selectedOptionDisplay={selectedOptionDisplay}
                    updateSelection={updateSelection}
                  />
                </li>
              );
            })}
            {selectedOptionDisplay != "" &&
            props.options.filter(
              (option) =>
                props.getOptionDisplay(option) !== selectedOptionDisplay,
            ).length === props.options.length &&
            !props.withoutSuggestion ? (
              <>
                {props.options.length > 0 ? (
                  <li
                    tabIndex={-1}
                    className="tw-cursor-default tw-py-[10px] tw-text-center tw-text-[12px] tw-text-gray-3"
                  >
                    <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[10px]">
                      <div className="tw-h-[0px] tw-grow tw-basis-0 tw-border-[0.5px] tw-border-solid tw-border-gray-5"></div>
                      <div className="tw-font-dm-sans tw-font-medium tw-text-gray-3">
                        OR
                      </div>
                      <div className="tw-h-[0px] tw-grow tw-basis-0 tw-border-[0.5px] tw-border-solid tw-border-gray-5"></div>
                    </div>
                  </li>
                ) : null}
                <li
                  key="custom"
                  className="tw-relative tw-block tw-min-h-[40px] tw-overflow-hidden tw-break-words tw-p-[10px] tw-align-middle tw-text-[14px] tw-text-gray-3 hover:tw-bg-blue-light-1"
                  onMouseDown={() => updateSelection(selectedOptionDisplay)}
                >
                  Enter{" "}
                  <span className="tw-font-bold tw-text-gray-0">
                    {selectedOptionDisplay}
                  </span>{" "}
                  as a custom entry
                </li>
              </>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
